// Sessie over tabbladen zonder de tokens naar localStorage te verhuizen.
//
// De MSAL-cache blijft sessionStorage (per tabblad, weg bij sluiten; P0 5 sep FE-03).
// Een nieuw tabblad heeft dus geen tokens, maar wél de Entra-sessiecookie van
// login.microsoftonline.com. Daarmee loggen we bij het opstarten stil in:
//   1. ssoSilent (verborgen iframe) — werkt waar third-party cookies mogen;
//   2. lukt dat niet (bv. Safari/ITP): één keer per tabblad een top-level redirect met
//      prompt=none. Entra stuurt direct terug: ingelogd, of een fout (geen sessie) →
//      dan de gewone inlogkaart. Nooit een lus, nooit een dubbele prompt.
// Identiteit: alleen stil inloggen als dít apparaat weet wie hier het laatst inlogde
// (loginHint, alleen het e-mailadres — geen token) en het resultaat moet dat account
// zijn. Zo pakt een nieuw tabblad nooit stilletjes de Entra-sessie van een collega.
// Uitloggen in één tabblad beëindigt de sessie in alle tabbladen (BroadcastChannel,
// met een storage-event als terugval).

import { loginRequest } from "../authConfig";
import { loginStartPagina } from "./veiligPad";

// Vlag per tabblad: "1" = stil inloggen is hier al geprobeerd; "uitgelogd" = er is
// (hier of in een ander tabblad) uitgelogd, een lopende stille login telt niet meer.
export const STIL_GEPROBEERD = "ff-auth-stil-geprobeerd";
// Per tabblad: welk account de prompt=none-redirect hoort op te leveren.
const STIL_VERWACHT = "ff-auth-stil-verwacht";
// Per apparaat (localStorage): wie hier het laatst inlogde. Alleen de gebruikersnaam.
export const LAATSTE_ACCOUNT = "ff-auth-laatste-account";
// Per apparaat (localStorage): tokenvrije uitlog-marker voor het storage-event.
const UITLOG_MARKER = "ff-auth-uitgelogd";
// Per tabblad: de uitlog-generatie (waarde van UITLOG_MARKER) bij de start van de poging.
const STIL_GENERATIE = "ff-auth-stil-generatie";
// Uitgelogd tijdens deze paginaload — ook als sessionStorage onbruikbaar is.
let uitgelogdInGeheugen = false;
// Alleen voor tests: elke test begint als een verse paginaload.
export function _verseLaadVoorTests() { uitgelogdInGeheugen = false; }
const KANAAL = "ff-auth";

// Pagina's waar we nooit automatisch inloggen: publieke klantlinks, dev-login, uitloggen.
const ZONDER_STIL = [/^\/deel\//, /^\/selectie\//, /^\/dev-login(\/|$)/, /^\/logout(\/|$)/];

function lees(sleutel, opslag = "sessionStorage") {
  try { return window[opslag].getItem(sleutel); } catch { return null; }
}
function zet(sleutel, waarde, opslag = "sessionStorage") {
  try { window[opslag].setItem(sleutel, waarde); } catch { /* privé-venster e.d. */ }
}
function wis(sleutel, opslag = "sessionStorage") {
  try { window[opslag].removeItem(sleutel); } catch { /* ignore */ }
}
const naam = (acc) => String(acc?.username || "").trim().toLowerCase();

export function blokkeerStilInloggen() {
  if (lees(STIL_GEPROBEERD) !== "uitgelogd") zet(STIL_GEPROBEERD, "1");
}
function markeerUitgelogd() {
  uitgelogdInGeheugen = true;
  zet(STIL_GEPROBEERD, "uitgelogd");
  // STIL_VERWACHT blijft staan: een lopende redirect-controle moet het resultaat afwijzen.
}
const generatie = () => lees(UITLOG_MARKER, "localStorage") || "";
// Uitgelogd sinds de start van de poging? In dit tabblad (geheugen/sessionStorage) of in
// een ander tabblad, ook als we dat event gemist hebben (redirect): generatie vergelijken.
const isUitgelogd = () => uitgelogdInGeheugen
  || lees(STIL_GEPROBEERD) === "uitgelogd"
  || (lees(STIL_GENERATIE) !== null && lees(STIL_GENERATIE) !== generatie());

// Onthoud wie hier inlogde (na een expliciete login of een gecontroleerde stille login).
export function onthoudAccount(account) {
  const n = naam(account);
  if (n) zet(LAATSTE_ACCOUNT, n, "localStorage");
}

function inIframe() {
  try { return window.self !== window.top; } catch { return true; }
}

// Mag dit tabblad nu een stille login proberen?
export function magStilInloggen(instance, pad = window.location.pathname) {
  if (inIframe()) return false; // MSAL's eigen verborgen iframe laadt ook deze app
  if (lees(STIL_GEPROBEERD)) return false;
  if (ZONDER_STIL.some((re) => re.test(pad || "/"))) return false;
  if ((instance.getAllAccounts?.() || []).length > 0) return false;
  if (!lees(LAATSTE_ACCOUNT, "localStorage")) return false; // geen bekende identiteit → inlogkaart
  return true;
}

// Afgewezen stille login: cache weg. De aanroeper herlaadt daarna de pagina, zodat de
// React-accountstate (msal-react) gegarandeerd opnieuw uit de lege cache wordt opgebouwd.
export const VERWORPEN = "verworpen";
async function verwerpLogin(instance) {
  try { await instance.clearCache(); } catch { /* ignore */ }
  return VERWORPEN;
}

/**
 * Probeer stil in te loggen. Resolvet `true` bij succes, `false` als de inlogkaart
 * getoond moet worden, `VERWORPEN` als een binnengekomen account is afgewezen (cache
 * gewist; aanroeper herlaadt). Bij de redirect-terugval navigeert de pagina weg en resolvet
 * de promise niet (de spinner blijft staan tot Entra terugstuurt).
 */
export async function probeerStilInloggen(instance, { redirectUri } = {}) {
  blokkeerStilInloggen(); // vóór alles: één poging per tabblad, ook als er iets misgaat
  const hint = lees(LAATSTE_ACCOUNT, "localStorage");
  if (!hint) return false;
  zet(STIL_GENERATIE, generatie());
  try {
    const res = await instance.ssoSilent({ ...loginRequest, loginHint: hint });
    // Intussen uitgelogd (ander tabblad)? Of een ander account dan verwacht? Weggooien.
    if (isUitgelogd() || naam(res?.account) !== hint) return verwerpLogin(instance);
    instance.setActiveAccount(res.account);
    return true;
  } catch {
    // Geen sessie zichtbaar in het iframe. Dat kan "echt geen sessie" zijn, maar ook
    // geblokkeerde third-party cookies (Safari/ITP geeft dan óók login_required); dat
    // is niet te onderscheiden. Daarom één top-level poging met prompt=none: Entra
    // vraagt dan nooit iets, het stuurt meteen terug (ingelogd of met een fout).
  }
  if (isUitgelogd()) return false;
  // Alleen redirecten als de eenmaligheidsvlag echt bewaard is — anders kan het een lus worden.
  zet(STIL_VERWACHT, hint);
  if (lees(STIL_GEPROBEERD) !== "1" || lees(STIL_VERWACHT) !== hint) return false;
  try {
    await instance.loginRedirect({
      ...loginRequest,
      prompt: "none",
      loginHint: hint,
      ...(redirectUri ? { redirectUri } : {}),
      redirectStartPage: loginStartPagina(),
    });
    return new Promise(() => {}); // pagina navigeert weg
  } catch {
    wis(STIL_VERWACHT);
    return false;
  }
}

// Loopt er in dit tabblad een prompt=none-redirect waarvan het resultaat nog
// gecontroleerd moet worden? (Dan het account nog niet onthouden of tonen.)
export function heeftStilControle() {
  return !!lees(STIL_VERWACHT);
}

/**
 * Na terugkeer van de prompt=none-redirect: is het opgeleverde account wie we
 * verwachtten? Zo niet (of intussen uitgelogd): cache weg, inlogkaart.
 * Resolvet `true` als de sessie mag blijven; `false` = cache gewist, pagina herladen.
 */
export async function controleerStilResultaat(instance) {
  const verwacht = lees(STIL_VERWACHT);
  if (!verwacht) return true;
  const afwijzen = isUitgelogd();
  wis(STIL_VERWACHT);
  const accounts = instance.getAllAccounts?.() || [];
  if (accounts.length === 0) return true; // fout teruggekregen → gewoon de inlogkaart
  if (afwijzen || accounts.some((a) => naam(a) !== verwacht)) {
    await verwerpLogin(instance);
    return false;
  }
  return true;
}

// --- Uitloggen in alle tabbladen -------------------------------------------------------

let kanaal = null;
function getKanaal() {
  if (kanaal || typeof window === "undefined" || typeof window.BroadcastChannel !== "function") return kanaal;
  try { kanaal = new window.BroadcastChannel(KANAAL); } catch { kanaal = null; }
  return kanaal;
}

// Aanroepen bij LOGOUT_START in dít tabblad.
export function meldUitgelogd() {
  markeerUitgelogd(); // na uitloggen niet stil terug inloggen in dit tabblad
  wis(LAATSTE_ACCOUNT, "localStorage"); // en ook niet in een volgend nieuw tabblad
  try { getKanaal()?.postMessage({ type: "uitgelogd" }); } catch { /* ignore */ }
  // Terugval zonder BroadcastChannel: storage-event in de andere tabbladen (tokenvrij).
  zet(UITLOG_MARKER, String(Date.now()), "localStorage");
}

// Luistert naar uitloggen in een ander tabblad: lopende stille login ongeldig, tokens
// van dit tabblad weg, en opnieuw laden zodat de inlogkaart verschijnt.
export function luisterNaarUitloggen(instance, herlaad = () => window.location.reload()) {
  let bezig = false;
  const opUitloggen = async () => {
    markeerUitgelogd();
    if (bezig || (instance.getAllAccounts?.() || []).length === 0) return;
    bezig = true;
    try { await instance.clearCache(); } catch { /* ignore */ }
    herlaad();
  };
  const opBericht = (e) => { if (e?.data?.type === "uitgelogd") opUitloggen(); };
  const opStorage = (e) => { if (e?.key === UITLOG_MARKER && e.newValue) opUitloggen(); };
  const k = getKanaal();
  k?.addEventListener("message", opBericht);
  window.addEventListener("storage", opStorage);
  return () => {
    k?.removeEventListener("message", opBericht);
    window.removeEventListener("storage", opStorage);
  };
}
